import { Select } from 'antd';
import type { TFunction } from 'i18next';
import type { ThemeName } from './ThemeContext';

const { Option } = Select;

/**
 * The selectable themes, in the order both theme pickers list them.
 *
 * A theme lives in six places (`theme/index.ts`, the `ThemeName` union, `themeMap`, the two
 * pickers and the three locale files); this collapses two of them into one, so adding a theme
 * no longer means writing the same `<Option>` twice and leaving the pickers free to drift
 * apart. The label keys are not derived from the value -- `corporate-blue` reads
 * `corporateBlue` in the locale files -- so they are spelled out.
 */
export const THEME_CHOICES: { value: ThemeName; labelKey: string; descriptionKey: string }[] = [
    { value: 'default', labelKey: 'default', descriptionKey: 'defaultDesc' },
    { value: 'pro1', labelKey: 'pro1', descriptionKey: 'pro1Desc' },
    { value: 'pro2', labelKey: 'pro2', descriptionKey: 'pro2Desc' },
    { value: 'dark', labelKey: 'dark', descriptionKey: 'darkDesc' },
    { value: 'corporate-blue', labelKey: 'corporateBlue', descriptionKey: 'corporateBlueDesc' },
    { value: 'elegant-gold', labelKey: 'elegantGold', descriptionKey: 'elegantGoldDesc' },
    { value: 'gyurci', labelKey: 'gyurci', descriptionKey: 'gyurciDesc' },
    { value: 'custom', labelKey: 'custom', descriptionKey: 'customDesc' },
];

/**
 * The `<Option>` elements for a theme `<Select>`, as an array rather than a component: Ant
 * Design's Select reads its options off its children, so a wrapper component would hide them.
 */
export const themeSelectOptions = (t: TFunction) =>
    THEME_CHOICES.map(({ value, labelKey, descriptionKey }) => (
        <Option key={value} value={value}>
            {t(`settings.themes.${labelKey}`)} - {t(`settings.themes.${descriptionKey}`)}
        </Option>
    ));
